import type { SchoolReportInterface } from '@whizz/api-model';
import { ConsistencyEnum } from '@whizz/api-model';
import type {
  AverageReportType,
  GroupType,
  SchoolReportType,
  SchoolTopicChartType,
  SchoolTopicTableType,
  SchoolViewType,
} from '../pages/types';
import type { AverageMetrics } from '@whizz/api-model/src/interface/tx3/GroupReportMetricsInterface';
import type {
  GroupDataInterface,
  SchoolReportAverage,
  TopicProgressGroupInterface,
} from '@whizz/api-model/src/interface/tx3/SchoolReportInterface';
import type {
  TableReportGroupType,
  TableStudentType,
  TopicChartElementType,
  TopicChartType,
} from './groupTopicReportMapper';
import type NewTopicProgressInterface from '@whizz/api-model/src/interface/tx3/NewTopicProgressInterface';
import type {
  TopicProgressStudentInterface,
} from '@whizz/api-model/dist/@types/interface/tx3/GroupReportMetricsInterface';
import { round, toFloat } from '../util/numbers';
import { convertYearsToWeeks, getDiffWeeks } from '../util/dates';
import { repository } from '@whizz/api-client';
import { COLOR_PICKER_COLORS, YEAR_GROUP_COLORS } from '../util/vars';
import { APP_THEME } from '../styles/theme/styled/theme';
import { getDefaultTagColor } from '../util/styled';
import type { AllColorType } from '../util/types';

const initialAverageValue: AverageReportType = {
  mathsAgeAverage: 0,
  mathsAgeImprovementAverage: 0,
  progressionAverage: 0,
  qualityLearningTimeAverage: 0,
  consistencyAverage: 0,
  consistencyAverageName: ConsistencyEnum.OCCASIONAL,
  tickStatusAverage: 'red',
  studentStatusAverage: 'rookie',
  timeSpanAverage: 0,
  mathsAgeAtStartAverage: 0,
  tutorUsageAverage: 0,
  topicChallengeUsageAverage: 0,
  progressionPassRateAverage: 0,
  exerciseMarkAverage: 0,
  testMarkAverage: 0,
  actualAgeAverage: 0,
  minutesPerProgressionAverage: 0,
};

const initialSchoolReport: {
  schoolReport: SchoolReportType
  chartTopic: SchoolTopicChartType
  tableTopic: SchoolTopicTableType
} = {
  schoolReport: {
    classes: [],
    yearGroups: [],
    average: initialAverageValue,
    yearGroupsAverage: initialAverageValue,
  },
  chartTopic: {
    classes: [],
    yearGroups: [],
  },
  tableTopic: {
    classes: {
      mathsAgeAvg: 0,
      topics: [],
      groups: [],
    },
    yearGroups: {
      mathsAgeAvg: 0,
      topics: [],
      groups: [],
    },
  },
};

const getYearGroupColorByName = (name: string) => {
  const yearTag = name.length > 1 ? name.substring(1) : name;
  switch (yearTag) {
    case 'P': return YEAR_GROUP_COLORS[1];
    case 'F': return YEAR_GROUP_COLORS[2];
    default: return isNaN(Number(yearTag))
      ? YEAR_GROUP_COLORS.NO_YEAR_GROUP
      : `year${yearTag}` as AllColorType;
  }
};

const getLabelColor = (id: number, color: number | null | undefined, type: SchoolViewType, name: string) => APP_THEME.colors[type === 'year' ? getYearGroupColorByName(name) : COLOR_PICKER_COLORS[color ?? getDefaultTagColor(id)]];

export const mapSchoolReport = (schoolReport: SchoolReportInterface | null):
{
  schoolReport: SchoolReportType
  chartTopic: SchoolTopicChartType
  tableTopic: SchoolTopicTableType
} => {
  if (schoolReport === null) return initialSchoolReport;

  const mappedGroupsDataForTopicProgress = mapGroupDataForTopicProgress(schoolReport);

  return {
    schoolReport: mapSchoolReportValues(schoolReport),
    chartTopic: mapChartTopic(schoolReport),
    tableTopic: mapTableTopic(schoolReport, mappedGroupsDataForTopicProgress),
  };
};

type GroupDataForTP = { [id: number]: { mathsAge: number } };
type MappedGroupsDataForTopicProgressType = {
  classes: GroupDataForTP
  yearGroups: GroupDataForTP
};
const mapGroupDataForTopicProgress = (schoolReport: SchoolReportInterface): MappedGroupsDataForTopicProgressType => {
  const mappedGroupsDataForTopicProgress: MappedGroupsDataForTopicProgressType = { classes: {}, yearGroups: {} };

  schoolReport.classes.forEach((gr) => {
    mappedGroupsDataForTopicProgress.classes[gr.id] = {
      mathsAge: gr.averages.mathsAge,
    };
  });

  schoolReport.yearGroups.forEach((gr) => {
    mappedGroupsDataForTopicProgress.yearGroups[gr.id] = {
      mathsAge: gr.averages.mathsAge,
    };
  });

  return mappedGroupsDataForTopicProgress;
};

const mapSchoolReportValues = (schoolReport: SchoolReportInterface): {
  classes: (GroupType & AverageReportType)[]
  yearGroups: (GroupType & AverageReportType)[]
  average: AverageReportType
  yearGroupsAverage: AverageReportType
} => {
  const diffWeeks = getDiffWeeks(schoolReport.startDate, schoolReport.endDate);

  return {
    classes: mapGroups(schoolReport.classes, diffWeeks, 'class'),
    yearGroups: mapGroups(schoolReport.yearGroups, diffWeeks, 'year'),
    average: mapAverageValues(schoolReport.averageClasses, diffWeeks),
    yearGroupsAverage: mapAverageValues(schoolReport.averageYearGroups, diffWeeks),
  };
};

const mapGroups = (groups: GroupDataInterface[], diffWeeks: number, type: SchoolViewType): (GroupType & AverageReportType)[] => {
  return groups.map((gr) => {
    const { id, name, totalStudentsNum, assessedStudents, unassessedStudents, averages, color } = gr;
    const averagesMapped = mapAverageValues(averages, diffWeeks);

    return {
      id: id,
      fullName: name,
      color: getLabelColor(id, color, type, name),
      totalStudentsNum,
      assessedStudents,
      unassessedStudents,
      chartsData: {
        mathsAge: averagesMapped.mathsAgeAverage,
        mathsAgeImprovement: averagesMapped.mathsAgeImprovementAverage,
        progression: round(averagesMapped.progressionAverage, 2),
        qualityLearningTime: averagesMapped.qualityLearningTimeAverage,
        mathsAgeDelta: round(averagesMapped.mathsAgeAverage ? (averagesMapped.mathsAgeAverage - averagesMapped.actualAgeAverage) : 0, 2),
        initialAssessment: toFloat(((assessedStudents / totalStudentsNum) * 100).toFixed(0)),
      },
      ...averagesMapped,
    };
  });
};

const mapAverageValues = (av: AverageMetrics | SchoolReportAverage, diffWeeks: number): AverageReportType => ({
  mathsAgeAverage: av.mathsAge,
  mathsAgeImprovementAverage: convertYearsToWeeks(av.mathsAgeImprovement),
  progressionAverage: av.progressionsPerWeek,
  qualityLearningTimeAverage: av.learningTime,
  consistencyAverage: av.consistency,
  consistencyAverageName: repository.GroupReportMetricsRepository.getConsistencyName(av.consistency, diffWeeks),
  tickStatusAverage: av.tickStatus,
  studentStatusAverage: av.studentStatus,
  timeSpanAverage: av.timeSpan,
  mathsAgeAtStartAverage: av.mathsAgeAtStart,
  tutorUsageAverage: av.tutorUsage,
  topicChallengeUsageAverage: av.topicChallengeUsage,
  progressionPassRateAverage: av.progressionPassRate,
  exerciseMarkAverage: av.exerciseMark,
  testMarkAverage: av.testMark,
  actualAgeAverage: av.actualAge,
  minutesPerProgressionAverage: av.minutesPerProgression,
});

const mapChartTopic = (schoolReport: SchoolReportInterface): {
  classes: TopicChartType[]
  yearGroups: TopicChartType[]
} => {
  return {
    classes: mapChartTopicGroups(
      schoolReport.topicProgressClasses,
      schoolReport.averageTopicProgressClasses,
      'class',
    ),
    yearGroups: mapChartTopicGroups(
      schoolReport.topicProgressYearGroups,
      schoolReport.averageTopicProgressYearGroups,
      'year',
    ),
  };
};

const mapChartTopicGroups = (
  topicProgressGroups: TopicProgressGroupInterface[],
  avgTopicProgressGroups: NewTopicProgressInterface[],
  type: SchoolViewType,
): TopicChartType[] => {
  return avgTopicProgressGroups.map((topicProgressAvg) => ({
    index: topicProgressAvg.topic.index,
    name: topicProgressAvg.topic.name,
    startAvgTopicAge: topicProgressAvg.startValue,
    endAvgTopicAge: topicProgressAvg.endValue,
    elements: topicProgressGroups
      .reduce((acc, groupTP) => {
        const topicProgress = groupTP.topicProgress.find((topicProgress) => topicProgress.topic.index === topicProgressAvg.topic.index);
        const { id, name, color } = groupTP;
        if (null == topicProgress) {
          return acc;
        }
        return [
          ...acc,
          {
            id: id,
            fullName: name,
            gender: null,
            color: getLabelColor(id, color, type, name),
            startTopicAge: topicProgress.startValue,
            progressTopicAge: topicProgress.progressValue,
            endTopicAge: topicProgress.endValue,
          },
        ];
      }, [] as TopicChartElementType[]),
  }));
};

const mapTableTopic = (
  schoolReport: SchoolReportInterface,
  mappedGroupsDataForTopicProgress: MappedGroupsDataForTopicProgressType,
): SchoolTopicTableType => {
  return {
    classes: mapTableTopicGroups(
      schoolReport.topicProgressClasses,
      schoolReport.averageTopicProgressClasses,
      mappedGroupsDataForTopicProgress.classes,
      schoolReport.averageClasses.mathsAge,
    ),
    yearGroups: mapTableTopicGroups(
      schoolReport.topicProgressYearGroups,
      schoolReport.averageTopicProgressYearGroups,
      mappedGroupsDataForTopicProgress.yearGroups,
      schoolReport.averageYearGroups.mathsAge,
    ),
  };
};

const mapTableTopicGroups = (
  topicProgressGroups: TopicProgressGroupInterface[],
  avrTopicProgressGroups: NewTopicProgressInterface[],
  groupDataForTopicProgress: GroupDataForTP,
  schoolMathAge: number,
): TableReportGroupType => {
  return {
    mathsAgeAvg: round(schoolMathAge, 2),
    topics: avrTopicProgressGroups.map((avrTP) => ({
      index: avrTP.topic.index,
      name: avrTP.topic.name,
      topicAgeAvg: round(avrTP.endValue, 2),
    })),
    groups: topicProgressGroups.map((gr) => mapTableTopicProgress(gr, groupDataForTopicProgress)),
  };
};

const mapTableTopicProgress = (
  groupTP: TopicProgressStudentInterface,
  groupDataForTopicProgress: GroupDataForTP,
): TableStudentType => {
  const tableGroup: TableStudentType = {
    id: groupTP.id,
    name: groupTP.name,
    mathsAge: round(groupDataForTopicProgress[groupTP.id].mathsAge, 2),
  };

  groupTP.topicProgress.forEach((tp) => {
    const topicProperty = `topic${tp.topic.index}`;
    tableGroup[topicProperty] = tp.endValue;
  });

  return tableGroup;
};
